import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Polygon } from 'react-leaflet';
import classnames from 'classnames';

import { IArea, IStateAwareArea, IStateAwareAreaWithHitbox } from 'types/map';
import { xy } from 'components/Map/util';
import { connect } from 'react-redux';
import { IAppState } from 'types/app';
// import chooseHitboxForArea from 'components/Map/util/chooseHitboxForArea';

const DRAG_THRESHOLD = 10; // px movement before we treat this as a drag

export interface Props {
  area: IStateAwareAreaWithHitbox,
  areas?: IArea[],
  onAreaSelect: (arg0?: IArea) => void,
  onClick: (area: IStateAwareArea) => void,
  zoomLevel: number,
}

export function Hitbox({
  area,
  onAreaSelect,
  zoomLevel,
  onClick: onParentClick,
}: Props) {
  // TODO: Narrow the type here
  const ref = useRef<any>(null);

  // const hitbox = chooseHitboxForArea(area, zoomLevel);
  // const { hitbox } = area;
  const hitbox = area.getHitboxForZoomLevel(zoomLevel);

  const [isDragging, setisDragging] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [isTouchActive, setIsTouchActive] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);

  const [isTouchDeactivationDeferred, setIsTouchDeactivationDeferred] = useState(false);

  const onClick = useCallback((e: any) => {
    if (e._simulated) {
      return;
    }

    if (isDragging) {
      return;
    }

    if (isTouchDeactivationDeferred) {
      setIsTouchDeactivationDeferred(false);
      setIsTouchActive(false);
      return;
    }

    onParentClick(area);
  }, [area, isTouchDeactivationDeferred, isDragging, onParentClick]);

  const onMouseDown = useCallback((e: MouseEvent) => {
    setIsMouseDown(true);
    setisDragging(false);
    setStartX(e.clientX);
    setStartY(e.clientY);
  }, []);

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging || !isMouseDown) {
      return;
    }
    const { clientX, clientY } = e;
    if (Math.abs(clientX - startX) > DRAG_THRESHOLD || Math.abs(clientY - startY) > DRAG_THRESHOLD) {
      setisDragging(true);
    }
  }, [isDragging, isMouseDown, startX, startY]);

  const onMouseOver = useCallback((e) => {
    // Why are we getting mouse overs when touch happens?
    if (e.sourceCapabilities?.firesTouchEvents ?? false) {
      return;
    }
    onAreaSelect(area);
  }, [area, onAreaSelect]);
  const onMouseOut = useCallback(() => onAreaSelect(), [onAreaSelect]);
  const onMouseUp = useCallback(() => setIsMouseDown(false), []);

  const onTouchEnd = useCallback(() => {
    if (isTouchActive) {
      setIsTouchDeactivationDeferred(true); // We will need to deactivate touch in the onClick phase
    }
  }, [isTouchActive]);

  const onTouchStart = useCallback(() => {
    setIsTouchActive(true);
  }, []);

  useEffect(() => {
    if (ref.current !== null) {

      const polygon = ref.current.leafletElement;
      const domElement = polygon.getElement();

      domElement.addEventListener('click', onClick);
      domElement.addEventListener('mousedown', onMouseDown);
      domElement.addEventListener('mousemove', onMouseMove);
      domElement.addEventListener('mouseover', onMouseOver);
      domElement.addEventListener('mouseout', onMouseOut);
      domElement.addEventListener('mouseup', onMouseUp);

      domElement.addEventListener('touchend', onTouchEnd);
      domElement.addEventListener('touchstart', onTouchStart);

      return () => {
        domElement.removeEventListener('click', onClick);
        domElement.removeEventListener('mousedown', onMouseDown);
        domElement.removeEventListener('mousemove', onMouseMove);
        domElement.removeEventListener('mouseover', onMouseOver);
        domElement.removeEventListener('mouseout', onMouseOut);
        domElement.removeEventListener('mouseup', onMouseUp);

        domElement.removeEventListener('touchend', onTouchEnd);
        domElement.removeEventListener('touchstart', onTouchStart);
      };
    }
  }, [
    ref,
    onClick,
    onMouseDown,
    onMouseMove,
    onMouseOver,
    onMouseOut,
    onMouseUp,
    onTouchEnd,
    onTouchStart,
  ]);

  return (
    <Polygon
      className={classnames(area.isLit ? "leaflet-interactive--hitbox" : 'leaflet-interactive--dark-hitbox')}
      ref={ref}
      opacity={0}
      fillOpacity={0}
      positions={hitbox.map((t: number[]) => xy(t[0], t[1]))}
    />
  );
}

Hitbox.displayName = 'Hitbox';

const mapStateToProps = ({ map: { areas } }: IAppState) => ({ areas });

export default connect(mapStateToProps)(Hitbox);
